import { accountsUrl } from '../pages/accounts/constants';
import { organizationEventsPath } from '../pages/systemSettings/addOrganizationToEvents/helpers';
import get from 'lodash.get';
import { pageTypesStrings } from '../constants/pageTypes';
import { buildFilteredListUrl } from '../helpers/buildFilteredListUrl';

export const CONTEXT_MENU_KEYS = {
  //-------------------------CampaignTable
  CAMPAIGN_MENU: {
    VIEW_DASHBOARD: {
      key: 'viewDashboard',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/dashboard/${item.id}`;
      }
    },
    VIEW_PERFORMANCE: {
      key: 'viewPerformance',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/performance/${item.id}`;
      }
    },
    EDIT_CAMPAIGN: {
      key: 'editCampaign',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/edit/${item.id}/setup`;
      }
    },
    VIEW_PUBLIC_PAGE: {
      key: 'viewPublicPage',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_SALES: {
      key: 'viewSales',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/sales/${item.id}`;
      }
    },
    VIEW_TICKETS: {
      key: 'viewTickets',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/tickets/${item.id}`;
      }
    },
    VIEW_MERCHANDISE: {
      key: 'viewMerchandise',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/merchandise/${item.id}`;
      }
    },
    VIEW_DONATIONS: {
      key: 'viewDonations',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/donations/${item.id}`;
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'viewFundraisers',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/fundraisers/${item.id}`;
      }
    },
    CLONE_CAMPAIGN: {
      key: 'cloneCampaign',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/clone/${item.id}/setup`;
      }
    },
    CHECK_IN: {
      key: 'checkIn',
      writeAction: true,
      getUrl(id: any, _params: any) {
        return `/campaigns/${id}/check-in`;
      }
    },
    AUDIT_TRAIL: {
      key: 'campaign_auditTrail',
      getUrl(item: { id: any }, params?: { paramName?: 'targetCampaignId' | 'targetEventId' }) {
        const paramName = params?.paramName ?? 'targetCampaignId';
        return `/system-settings/audit-trail?${paramName}=${item.id}`;
      }
    }
  },

  //-------------------------AccountDetailTable
  ACCOUNT_DETAILS: {
    VIEW_DONATION: {
      key: 'donations_view',
      getUrl(item: { id: any }, _params: any) {
        return `/donations/view/${item.id}`;
      }
    },
    EDIT_DONATION: {
      key: 'account_detail_editDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donations/edit/${item.id}/donation-detail`;
      }
    },
    EDIT_PAGE: {
      key: 'account_detail_editPage',
      writeAction: true,
      getUrl(
        item: { type: any; fundraiserId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/fundraiser/edit/${item.fundraiserId}/page-detail`;
          case pageTypesStrings.TEAM:
            return `/campaigns/team/edit/${item.teamId}/page-detail`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/collection/edit/${item.collectionId}/page-detail`;
        }
      }
    },
    NAVIGATE_TO_PUBLIC: {
      key: 'account_detail_navigateToPublicSite',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_PAGE_DONATIONS: {
      key: 'account_detail_viewPageDonations',
      getUrl(
        item: {
          fundraiser: any;
          campaignId: any;
          fundraiserId: any;
          teamId: any;
        },
        _params: any
      ) {
        if (item.fundraiser) {
          return `/campaigns/${item.campaignId}/fundraiser/view/donations/${item.fundraiserId}`;
        } else {
          return `/campaigns/${item.campaignId}/team/view/donations/${item.teamId}`;
        }
      }
    },    
    VIEW_PAGE_FUNDRAISERS: {
      key: 'account_detail_viewPageFundraisers',
      getUrl(item: { campaignId: any; teamId: any }, _params: any) {
        return `/campaigns/${item.campaignId}/team/view/fundraisers/${item.teamId}`;
      }
    },
    VIEW_FUNDRAISER: {
      key: 'view_fundraiser',
      getUrl(item: { id: any; fundraiserId?: any; teamId?: any; collectionId?: any; type?: any }, _params: any) {
        // Prefer explicit type; fallback to presence of specific ids
        const type = item.type;
        if (type) {
          switch (type) {
            case pageTypesStrings.TEAM:
              return `/fundraisers/team/view/${item.teamId || item.id}`;
            case pageTypesStrings.COLLECTION:
              return `/fundraisers/collection/view/${item.collectionId || item.id}`;
            default:
              return `/fundraisers/view/${item.id || item.fundraiserId}`;
          }
        }
        if (item.collectionId) return `/fundraisers/collection/view/${item.collectionId}`;
        if (item.teamId) return `/fundraisers/team/view/${item.teamId}`;
        return `/fundraisers/view/${item.id || item.fundraiserId}`;
      }
    },
    IMPERSONATE_FUNDRAISER: {
      key: 'account_detail_impersonateFundraiser',
      writeAction: true
    },
    REFUND_DONATION: {
      key: 'account_detail_refundDonation',
      writeAction: true
    },
    DELETE_DONATION: {
      key: 'account_detail_deleteDonation',
      writeAction: true,
    },
    REISSUE_RECEIPT: {
      key: 'account_detail_reissueReceipt',
      writeAction: true
    },
    VIEW_SALE: {
      key: 'account_detail_viewSale',
      getUrl(item: { id: any }, _params: any) {
        return `/sales/view/${item.id}`;
      }
    },
    EDIT_SALE: {
      key: 'account_detail_editSale',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/sales/edit/${item.id}/invoice-information`;
      }
    },
    VIEW_DONATION_SUBSCRIPTION: {
      key: 'account_detail_viewDonationSubscription',
      getUrl(item: { id: any }, _params: any) {
        return `/recurring/detail/${item.id}/donations`;
      }
    },
    EDIT_DONATION_SUBSCRIPTION: {
      key: 'account_detail_editDonationSubscription',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/recurring/edit/${item.id}/recurring-detail`;
      }
    },
    VIEW_PLACEHUB_APPLICATION: {
      key: 'account_detail_viewPlacehubApplication',
      getUrl(item: { id: any }, _params: any) {
        return `/vip-program/vip/${item.id}/part-one`;
      }
    },
    MARK_AS_SPAM: {
      key: 'account_detail_markAsSpam',
      writeAction: true
    }
  },

  //-------------------------AccountTable
  ACCOUNT: {
    VIEW_PAGE: {
      key: 'account_view',
      getUrl(item: { id: any }, params: { selectedView: string | number }) {
        return `/accounts/detail/${item.id}`;
      }
    },
    RETENTION_VIEW_ACCOUNT: {
      key: 'retention_view_account',
      getUrl(item: { accountId: any }, _params: any) {
        return `/accounts/detail/${item.accountId}`;
      }
    },
    // No getUrl: opens the retention panel in place, keyed on the flag row, so it works for
    // subjects with no account of their own (guest donors and ticket buyers, leads, ambassadors).
    RETENTION_VIEW_RULES: {
      key: 'retention_view_rules'
    },
    // Both open a confirmation in place rather than navigating, and both are writeAction so a
    // read-only viewer never sees them even though the tab itself is sysadmin-gated.
    RETENTION_DEIDENTIFY: {
      key: 'retention_deidentify',
      writeAction: true
    },
    RETENTION_REVERT: {
      key: 'retention_revert',
      writeAction: true
    },
    // Read-only, so it stays available to a viewer unlike the two actions above.
    RETENTION_AUDIT: {
      key: 'retention_audit'
    },
    EDIT_PAGE: {
      key: 'account_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/accounts/settings/${item.id}`;
      }
    },
    IMPERSONATE: {
      key: 'account_impersonate',
      writeAction: true,
    },
    AUDIT_TRAIL: {
      key: 'account_audit_trail',
      getUrl(item: { id: any }, _params?: any) {
        return `/system-settings/audit-trail?accountId=${item.id}`;
      }
    }
  },

  //-------------------------DashboardTable
  DASHBOARD: {
    VIEW_DONATION: {
      key: 'donations_view',
      getUrl(item: { id: any }, _params: any) {
        return `/donations/view/${item.id}`;
      }
    },
    EDIT_DONATION: {
      key: 'dashboard_editDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donations/edit/${item.id}/donation-detail`;
      }
    },
    REFUND_DONATION: {
      key: 'dashboard_refundDonation',
      writeAction: true,
    },
    DELETE_DONATION: {
      key: 'dashboard_deleteDonation',
      writeAction: true,
    },
    REISSUE_RECEIPT: {
      key: 'dashboard_reissueReceipt',
      writeAction: true,
    },
    EDIT_PAGE: {
      key: 'dashboard_editPage',
      writeAction: true,
      getUrl(
        item: { type: any; fundraiserId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/fundraiser/edit/${item.fundraiserId}/page-detail`;
          case pageTypesStrings.TEAM:
            return `/campaigns/team/edit/${item.teamId}/page-detail`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/collection/edit/${item.collectionId}/page-detail`;
        }
      }
    },
    VIEW_PUBLIC: {
      key: 'dashboard_navigateToPublicSite',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_DONATIONS: {
      key: 'dashboard_viewPageDonations',
      getUrl(
        item: {
          type: any;
          campaignId: any;
          fundraiserId: any;
          teamId: any;
          collectionId: any;
        },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/${item.campaignId}/fundraiser/view/donations/${item.fundraiserId}`;
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/donations/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/donations/${item.collectionId}`;
        }
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'dashboard_viewPageFundraisers',
      getUrl(
        item: { type: any; campaignId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/fundraisers/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/fundraisers/${item.collectionId}`;
        }
      }
    },
    EDIT_SALE: {
      key: 'dashboard_editSale',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/sales/edit/${item.id}/invoice-information`;
      }
    },
    REFUND_SALE: {
      key: 'dashboard_refundSale',
      writeAction: true,
    },
    REISSUE_INVOICE: {
      key: 'dashboard_reissueInvoice',
      writeAction: true,
    }
  },

  //-------------------------CampaignDetailsTable
  CAMPAIGN_DETAIL: {
    VIEW_DONATION: {
      key: 'donations_view',
      getUrl(item: { id: any }, _params: any) {
        return `/donations/view/${item.id}`;
      }
    },
    EDIT_DONATION: {
      key: 'campaign_detail_editDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donations/edit/${item.id}/donation-detail`;
      }
    },
    EDIT_SALE: {
      key: 'campaign_detail_editSale',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/sales/edit/${item.id}/invoice-information`;
      }
    },
    EDIT_TICKET: {
      key: 'campaign_detail_editTicket',
      writeAction: true,
      getUrl(item: { sale: { id: any }; type: any; id: any }, _params: any) {
        return `/sales/edit/${item.sale.id}/${item.type}-${item.id}`;
      }
    },
    EDIT_MERCHANDISE: {
      key: 'campaign_detail_editMerchandise',
      writeAction: true,
      getUrl(item: { sale: { id: any }; type: any; id: any }, _params: any) {
        return `/sales/edit/${item.sale.id}/${item.type}-${item.id}`;
      }
    },
    TRANSFER_DONATION: {
      key: 'campaign_detail_transferDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donation/${item.id}/transfer/setup`;
      }
    },
    REFUND_DONATION: {
      key: 'campaign_detail_refundDonation',
      writeAction: true,
    },
    DELETE_DONATION: {
      key: 'campaign_detail_deleteDonation',
      writeAction: true,
    },
    REFUND_SALE: {
      key: 'campaign_detail_refundSale',
      writeAction: true,
    },
    REFUND_TICKET: {
      key: 'campaign_detail_refundTicket',
      writeAction: true,
    },
    REISSUE_RECEIPT: {
      key: 'campaign_detail_reissueReceipt',
      writeAction: true,
    },
    REISSUE_INVOICE: {
      key: 'campaign_detail_reissueInvoice',
      writeAction: true,
    },
    REISSUE_TICKET: {
      key: 'campaign_detail_reissueTicket',
      writeAction: true,
    },
    EDIT_PAGE: {
      key: 'campaign_detail_editPage',
      writeAction: true,
      getUrl(
        item: { type: any; fundraiserId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/fundraiser/edit/${item.fundraiserId}/page-detail`;
          case pageTypesStrings.TEAM:
            return `/campaigns/team/edit/${item.teamId}/page-detail`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/collection/edit/${item.collectionId}/page-detail`;
        }
      }
    },
    VIEW_PUBLIC: {
      key: 'campaign_detail_navigateToPublicSite',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_TICKETS: {
      key: 'campaign_detail_viewPageTickets',
      getUrl(_item: any, _params: any) {
        //TODO
        return '';
      }
    },
    VIEW_SALE: {
      key: 'campaign_detail_viewPageSale',
      getUrl(item: { id: any }, _params: any) {
        return `/sales/view/${item.id}`;
      }
    },
    VIEW_LINKED_SALE: {
      key: 'campaign_detail_viewLinkedSale',
      getUrl(item: { sale: any }, _params: any) {
        return `/sales/view/${item.sale?.id}`;
      }
    },
    VIEW_DONATIONS: {
      key: 'campaign_detail_viewPageDonations',
      getUrl(
        item: {
          type: any;
          campaignId: any;
          fundraiserId: any;
          teamId: any;
          collectionId: any;
        },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/${item.campaignId}/fundraiser/view/donations/${item.fundraiserId}`;
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/donations/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/donations/${item.collectionId}`;
        }
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'campaign_detail_viewPageFundraisers',
      getUrl(
        item: { type: any; campaignId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/fundraisers/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/fundraisers/${item.collectionId}`;
        }
      }
    },
    VIEW_FUNDRAISER: {
      key: 'view_fundraiser',
      getUrl(item: { id: any; fundraiserId?: any; teamId?: any; collectionId?: any; type?: any }, _params: any) {
        const type = item.type;
        if (type) {
          switch (type) {
            case pageTypesStrings.TEAM:
              return `/fundraisers/team/view/${item.teamId || item.id}`;
            case pageTypesStrings.COLLECTION:
              return `/fundraisers/collection/view/${item.collectionId || item.id}`;
            default:
              return `/fundraisers/view/${item.id || item.fundraiserId}`;
          }
        }
        if (item.collectionId) return `/fundraisers/collection/view/${item.collectionId}`;
        if (item.teamId) return `/fundraisers/team/view/${item.teamId}`;
        return `/fundraisers/view/${item.id || item.fundraiserId}`;
      }
    }
  },//-------------------------FundraisersTable
  FUNDRAISERS: {
    VIEW_FUNDRAISER: {
      key: 'view_fundraiser',
      getUrl(item: { id: any; fundraiserId?: any; teamId?: any; collectionId?: any; type?: any }, _params: any) {
        const type = item.type;
        if (type) {
          switch (type) {
            case pageTypesStrings.TEAM:
              return `/fundraisers/team/view/${item.teamId || item.id}`;
            case pageTypesStrings.COLLECTION:
              return `/fundraisers/collection/view/${item.collectionId || item.id}`;
            default:
              return `/fundraisers/view/${item.id || item.fundraiserId}`;
          }
        }
        if (item.collectionId) return `/fundraisers/collection/view/${item.collectionId}`;
        if (item.teamId) return `/fundraisers/team/view/${item.teamId}`;
        return `/fundraisers/view/${item.id || item.fundraiserId}`;
      }
    },
    EDIT_PAGE: {
      key: 'fundraiser_detail_editPage',
      writeAction: true,
      getUrl(
        item: { type: any; fundraiserId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/fundraisers/fundraiser/edit/${item.fundraiserId}/page-detail`;
          case pageTypesStrings.TEAM:
            return `/fundraisers/team/edit/${item.teamId}/page-detail`;
          case pageTypesStrings.COLLECTION:
            return `/fundraisers/collection/edit/${item.collectionId}/page-detail`;
        }
      }
    },
    VIEW_PUBLIC: {
      key: 'fundraiser_detail_navigateToPublicSite',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_DONATIONS: {
      key: 'fundraiser_detail_viewPageDonations',
      getUrl(
        item: {
          type: any;
          campaignId: any;
          fundraiserId: any;
          teamId: any;
          collectionId: any;
        },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/${item.campaignId}/fundraiser/view/donations/${item.fundraiserId}`;
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/donations/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/donations/${item.collectionId}`;
        }
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'fundraiser_detail_viewPageFundraisers',
      getUrl(
        item: { type: any; campaignId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/fundraisers/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/fundraisers/${item.collectionId}`;
        }
      }
    },
    MARK_AS_SPAM: {
      key: 'fundraisers_markAsSpam',
      writeAction: true
    }
  },

  //-------------------------DonationsTable
  DONATIONS: {
    VIEW_DONATION: {
      key: 'donations_view',
      getUrl(item: { id: any }, _params: any) {
        return `/donations/view/${item.id}`;
      }
    },
    EDIT_DONATION: {
      key: 'donations_editDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donations/edit/${item.id}/donation-detail`;
      }
    },
    REFUND_DONATION: {
      key: 'donations_refundDonation',
      writeAction: true,
    },
    DELETE_DONATION: {
      key: 'donations_deleteDonation',
      writeAction: true,
    },
    VIEW_RECEIPT: {
      key: 'donations_viewReceipt',
      writeAction: true,
    },
    REISSUE_RECEIPT: {
      key: 'donations_reissueReceipt',
      writeAction: true,
    },
    VIEW_IN_STRIPE: {
      key: 'donations_viewInStripe',
    },
    TRANSFER_DONATION: {
      key: 'donations_transferDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donation/${item.id}/transfer/setup`;
      }
    },
    VIEW_ALL_DONOR_DONATIONS: {
      key: 'donations_viewAllDonorDonations',
      getUrl(item: { donorId: any }, _params: any) {
        return `/accounts/detail/${item.donorId}/donations`;
      }
    },
    VIEW_LINKED_SALE: {
      key: 'donations_viewLinkedSale',
      getUrl(item: { saleId: any }, _params: any) {
        return `/sales/view/${item.saleId}`;
      }
    },
    VIEW_LINKED_DONATION_SUBSCRIPTION: {
      key: 'donations_viewLinkedDonationSubscription',
      getUrl(item: { subscriptionId: any }, _params: any) {
        return `/recurring/detail/${item.subscriptionId}/donations`;
      }
    },
    AUDIT_TRAIL: {
      key: 'donations_auditTrail',
      getUrl(item: { id: any }, _params?: any) {
        return `/system-settings/audit-trail?targetDonationId=${item.id}`;
      }
    }
  },

  //-------------------------EventsTable
  EVENTS: {
    VIEW_DASHBOARD: {
      key: 'viewDashboard',
      getUrl(item: { id: any }, _params: any) {
        return `/events/dashboard/${item.id}`;
      }
    },
    VIEW_PERFORMANCE: {
      key: 'viewPerformance',
      getUrl(item: { id: any }, _params: any) {
        return `/events/performance/${item.id}`;
      }
    },
    EDIT: {
      key: 'events_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/events/edit/${item.id}/setup`;
      }
    },
    VIEW: {
      key: 'events_view',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_ORGANIZATIONS: {
      key: 'events_view_organizations',
      getUrl(item: { id: any }, _params: any) {
        return `/events/view/organizations/${item.id}`;
      }
    },
    VIEW_DONATIONS: {
      key: 'events_view_donations',
      getUrl(item: { id: any }, _params: any) {
        return `/events/view/donations/${item.id}`;
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'events_view_fundraisers',
      getUrl(item: { id: any }, _params: any) {
        return `/events/view/fundraisers/${item.id}`;
      }
    },
    CLONE_EVENT: {
      key: 'events_clone_event',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/events/clone/${item.id}/setup`;
      }
    },
    ACTIVE_INTEGRATION: {
      key: 'active_integration',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/events/active-integration/${item.id}/setup`;
      }
    },
    BACK_OFFICE: {
      key: 'backoffice',
      writeAction: true,
    }
  },

  //-------------------------EventDetailsTable
  EVENTDETAILS: {
    DASHBOARD_CAMPAIGN: {
      key: 'eventdetails_dashboardCampaign',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/performance/${item.id}`;
      }
    },
    EDIT_CAMPAIGN: {
      key: 'eventdetails_editCampaign',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/edit/${item.id}/setup`;
      }
    },
    VIEW_PUBLIC_PAGE: {
      key: 'eventdetails_viewPublicPage',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_DONATIONS: {
      key: 'eventdetails_viewDonations',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/donations/${item.id}`;
      }
    },
    VIEW_FUNDRAISERS: {
      key: 'eventdetails_viewFundraisers',
      getUrl(item: { id: any }, _params: any) {
        return `/campaigns/view/fundraisers/${item.id}`;
      }
    },
    EDIT_DONATION: {
      key: 'eventdetails_editDonation',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/donations/edit/${item.id}/donation-detail`;
      }
    },
    VIEW_DONATION: {
      key: 'donations_view',
      getUrl(item: { id: any }, _params: any) {
        return `/donations/view/${item.id}`;
      }
    },
    REFUND_DONATION: {
      key: 'eventdetails_refundDonation',
      writeAction: true,
    },
    DELETE_DONATION: {
      key: 'eventdetails_deleteDonation',
      writeAction: true,
    },
    REISSUE_RECEIPT: {
      key: 'eventdetails_reissueReceipt',
      writeAction: true,
    },
    EDIT_PAGE: {
      key: 'eventdetails_editPage',
      writeAction: true,
      getUrl(
        item: { type: any; fundraiserId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/fundraiser/edit/${item.fundraiserId}/page-detail`;
          case pageTypesStrings.TEAM:
            return `/campaigns/team/edit/${item.teamId}/page-detail`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/collection/edit/${item.collectionId}/page-detail`;
        }
      }
    },
    NAVIGATE_TO_PUBLIC: {
      key: 'eventdetails_navigateToPublicSite',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_PAGE_DONATIONS: {
      key: 'eventdetails_viewPageDonations',
      getUrl(
        item: {
          type: any;
          campaignId: any;
          fundraiserId: any;
          teamId: any;
          collectionId: any;
        },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.FUNDRAISER:
            return `/campaigns/${item.campaignId}/fundraiser/view/donations/${item.fundraiserId}`;
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/donations/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/donations/${item.collectionId}`;
        }
      }
    },
    VIEW_PAGE_FUNDRAISERS: {
      key: 'eventdetails_viewPageFundraisers',
      getUrl(
        item: { type: any; campaignId: any; teamId: any; collectionId: any },
        _params: any
      ) {
        switch (item.type) {
          case pageTypesStrings.TEAM:
            return `/campaigns/${item.campaignId}/team/view/fundraisers/${item.teamId}`;
          case pageTypesStrings.COLLECTION:
            return `/campaigns/${item.campaignId}/collection/view/fundraisers/${item.collectionId}`;
        }
      }
    }
  },

  //-------------------------OrganizationsTable
  ORGANIZATIONS: {
    VIEW_DASHBOARD: {
      key: 'viewDashboard',
      getUrl(item: { id: any }, _params: any) {
        return `/organizations/dashboard/${item.id}`;
      }
    },
    EDIT: {
      key: 'organizations_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/organizations/edit/${item.id}/about`;
      }
    },
    ADD_TO_EVENTS: {
      key: 'organizations_add_to_events',
      writeAction: true,
      getUrl(item: { id: number }, _params: any) {
        return organizationEventsPath(item.id);
      }
    }
  },

  //-------------------------MerchandiseTable
  MERCHANDISE: {
    EDIT: {
      key: 'merchandise_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/merchandise/edit/${item.id}/merchandise-detail`;
      }
    }
  },

  //-------------------------RecurringTable
  RECURRING: {
    EDIT: {
      key: 'recurring_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/recurring/edit/${item.id}/recurring-detail`;
      }
    },
    CANCEL_DONATION: {
      key: 'recurring_close',
      writeAction: true
    },
    VIEW: {
      key: 'recurring_donations',
      getUrl(item: { id: any }, _params: any) {
        return `/recurring/detail/${item.id}/donations`;
      }
    },
    TRANSFER: {
      key: 'recurring_donation_transfer',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/recurring/transfer/${item.id}`;
      }
    },
    VIEW_PAGE_DONATIONS: {
      key: 'recurring_donations_refundDonation'
    },
    REFUND_DONATION: {
      key: 'recurring_refundDonation',
      writeAction: true,
    }
  },

  //-------------------------ProgramTable
  PROGRAM: {
    VIEW_EVENT_PROGRAMS: {
      key: 'event_programs',
      getUrl(item: { eventId: any }, _params: any) {
        return `/vip-program/event/${item.eventId}/programs`;
      }
    },
    VIEW_CAMPAIGN_PROGRAMS: {
      key: 'campaign_programs',
      getUrl(item: { campaignId: any }, _params: any) {
        return `/vip-program/campaign/${item.campaignId}/applications`;
      }
    },
    EDIT_EVENT_PROGRAM: {
      key: 'edit_event_program',
      writeAction: true,
      getUrl(item: { eventId: any }, _params: any) {
        return `/vip-program/edit/${item.eventId}/setup`;
      }
    },
    EDIT_CAMPAIGN_PROGRAM: {
      key: 'edit_campaign_program',
      writeAction: true,
      getUrl(item: { campaignId: any }, _params: any) {
        return `/vip-program/campaign/edit/${item.campaignId}/setup`;
      }
    },
    TOGGLE_CAMPAIGN_PROGRAMS: {
      key: 'toggle_campaign_program',
      writeAction: true,
    },
    CONFIRM_REGISTRATION: {
      key: 'confirm_registration',
      writeAction: true,
    },
    EDIT_APPLICATION: {
      key: 'viewedit_application',
      getUrl(item: { id: any }, _params: any) {
        return `/vip-program/application/${item.id}`;
      }
    },
    VIEW_APPLICATION_FORM: {
      key: 'view_application_form',
      getUrl(item: { urlFull: any }, _params: any) {
        return item.urlFull;
      },
      target: '_blank'
    },
    VIEW_FUNDRAISER: {
      key: 'view_fundraiser_page',
      getUrl(item: { fundraiser: { urlFull: any } }, _params: any) {
        return item.fundraiser.urlFull;
      },
      target: '_blank'
    },
    CHANGE_APPLICATION_LINKED_PAGE: {
      key: 'change_application_linked_page',
      writeAction: true,
    },
    APPLICATION_LINKED_PAGE_VIEW_DETAILS: {
      key: 'view_application_linked_page',
      getUrl(item: { fundraiserId: any }, _params: any) {
        return `/fundraisers/view/${item.fundraiserId}`;
      }
    },
    APPLICATION_LINKED_PAGE_EDIT: {
      key: 'edit_application_linked_page',
      writeAction: true,
      getUrl(item: { fundraiserId: any }, _params: any) {
        return `/fundraisers/fundraiser/edit/${item.fundraiserId}/page-detail`;
      }
    },
    AUDIT_TRAIL: {
      key: 'program_auditTrail',
      getUrl(item: { id: any }, _params?: any) {
        return `/system-settings/audit-trail?targetEventAmbassadorId=${item.id}`;
      }
    }
  },

  //-------------------------Webhook table
  WEBHOOK_TABLE: {
    EDIT: {
      key: 'webhook_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/webhooks/edit/${item.id}`;
      }
    },
    DELETE: {
      key: 'webhook_delete',
      writeAction: true,
    }
  },

  //-------------------------Terminal table
  TERMINAL_TABLE: {
    UNASSIGN: {
      key: 'terminal_unassign',
      writeAction: true,
    },
    ASSIGN: {
      key: 'terminal_assign',
      writeAction: true,
    },
    EDIT: {
      key: 'terminal_edit',
      writeAction: true,
    },
    DELETE: {
      key: 'terminal_delete',
      writeAction: true,
    }
  }
  ,

  //-------------------------Franchises table
  FRANCHISES: {
    EDIT: {
      key: 'franchise_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/system-settings/franchises/edit/${item.id}`;
      }
    },
    HUB: {
      key: 'franchise_hub',
      getUrl(item: { id: any }, _params: any) {
        return `/hub/${item.id}`;
      }
    },
    CAMPAIGNS: {
      key: 'franchise_campaigns',
      // Target the `all` tab directly; the bare `/campaigns` route redirects
      // through <Navigate to=…> which strips the query string we need.
      getUrl(item: { id: any; name?: string }, _params: any) {
        return buildFilteredListUrl('/campaigns/all', 'franchiseId', item.id, item.name);
      }
    },
    EVENTS: {
      key: 'franchise_events',
      getUrl(item: { id: any; name?: string }, _params: any) {
        return buildFilteredListUrl('/events/all', 'franchiseId', item.id, item.name);
      }
    }
  },

  //-------------------------Campaign organizers table
  CAMPAIGN_ORGANIZERS: {
    CAMPAIGNS: {
      key: 'campaign_organizer_campaigns',
      // Target the `all` tab directly; the bare `/campaigns` route redirects
      // through <Navigate to=…> which strips the query string we need.
      getUrl(item: { id: any; name?: string }, _params: any) {
        return buildFilteredListUrl('/campaigns/all', 'campaignOrganizerId', item.id, item.name);
      }
    }
  },

  //-------------------------Event organizers table
  EVENT_ORGANIZERS: {
    EVENTS: {
      key: 'event_organizer_events',
      getUrl(item: { id: any; name?: string }, _params: any) {
        return buildFilteredListUrl('/events/all', 'eventOrganizerId', item.id, item.name);
      }
    }
  },

  SALES: {
    VIEW: {
      key: 'sales_view',
      getUrl(item: { id: any }, _params: any) {
        return `/sales/view/${item.id}`;
      }
    },
    EDIT: {
      key: 'sales_edit',
      writeAction: true,
      getUrl(item: { id: any }, _params: any) {
        return `/sales/edit/${item.id}/invoice-information`;
      }
    },
    REFUND_OR_CANCEL: {
      key: 'sales_refundOrCancel',
      writeAction: true,
    },
    REISSUE_INVOICE: {
      key: 'sales_reissueInvoice',
      writeAction: true,
    },
    VIEW_IN_STRIPE: {
      key: 'sales_viewInStripe',
    },
    AUDIT_TRAIL: {
      key: 'sales_auditTrail',
      getUrl(item: { id: any }, _params?: any) {
        return `/system-settings/audit-trail?targetSaleId=${item.id}`;
      }
    }
  },

  GENERAL: {
    EXPORT_HISTORY: {
      key: 'export_history',
      getUrl(item: any, _params: any) {
        return `/export-history`;
      }
    },
    WEBHOOKS: {
      key: 'webhooks',
      getUrl(item: any, _params: any) {
        return `/webhooks`;
      }
    },
    SYSTEM_SETTINGS: {
      key: 'system_settings',
      getUrl(item: any, _params: any) {
        return `/system-settings`;
      }
    },
    SUPPORT_URL: {
      key: 'support_url',
      getUrl(item: any, _params: any) {
        return 'https://grassrootz.zendesk.com';
      },
      target: '_blank'
    }
  }
};

// Flat registry keyed by the entry's `.key` string.
// Allows ContextMenuItemImpl to look up any entry (including writeAction)
// without the caller having to pass contextMenuOptions.
const buildRegistry = (): Map<string, any> => {
  const map = new Map<string, any>();
  Object.values(CONTEXT_MENU_KEYS).forEach((group: any) => {
    Object.values(group).forEach((entry: any) => {
      if (entry && typeof entry === 'object' && entry.key) {
        map.set(entry.key, entry);
      }
    });
  });
  return map;
};

export const CONTEXT_MENU_REGISTRY = buildRegistry();
